import React from 'react';
import { Icon } from '../../../design/Icon';
import { fmt } from '../../../design/format';
import { formatInZone } from '../../../design/timeZone';
import { Badge, SeverityLabel } from '../../../components/shared/Badges';
import { LineChart } from '../../../components/shared/charts/LineChart';
import { asDate, fmtTime } from '../callFormat';
import { useTerms } from '../../../layout/ShellStatus';
import {
  SPEAKER_COLORS,
  callbackNumber,
  callerRoleLabel,
  languageName,
  methodLabel,
  providerLabel,
  redactionPhrase,
  sentimentNote,
  sentimentSeries,
  transcriptionLabel,
} from './callDetailModel';

const OWNER_LABEL = { Agent: 'Agent', Caller: 'Caller', Staff: 'Staff', Board: 'Board', Vendor: 'Vendor', Unknown: 'Unassigned' };
const FIELD = {
  callerName: 'caller.name',
  callback: 'caller.callbackNumber',
  email: 'caller.email',
  account: 'caller.accountReference',
  role: 'caller.role',
  community: 'property.communityName',
  street: 'property.streetAddress',
  unit: 'property.unitOrLot',
};

function Quote({ quote, markId, speakerName, onQuote }) {
  if (!quote) return null;
  const text = quote.quote.length > 90 ? `${quote.quote.slice(0, 88)}…` : quote.quote;
  return (
    <button className="evq" type="button" data-testid="evidence-link" data-ev={markId || undefined} onClick={() => onQuote(quote, markId)}>
      <Icon name="quote" className="ico-sm" />
      <span>
        <span className="q">“{text}”</span> <span className="who">{speakerName(quote)} {fmt.mmss(quote.startMs / 1000)}</span>
      </span>
    </button>
  );
}

const retentionText = (transcript, analysis) => {
  const until = analysis?.retainUntil || transcript?.retainUntil;
  if (!until) return 'Not set';
  const from = transcript?.createdAt || analysis?.analyzedAt;
  const months = from ? Math.round((asDate(until) - asDate(from)) / (86400000 * 30.44)) : null;
  return `${formatInZone(asDate(until), { month: 'short', day: 'numeric', year: 'numeric' })}${months ? ` (${months}-month retention)` : ''}`;
};

/** "AI analysis" panel (mockup 13-call-detail.html): every value links to the quote it came from, both ways. */
export function AiPanel({ call, analysis, transcript, taxonomyName, quoteMark, speakerName, activeMark, onQuote, onMarkEnter, onMarkLeave, actions }) {
  const terms = useTerms();
  const evidence = analysis.evidence || [];
  const quoteFor = (field) => evidence.find((q) => q.field === field);
  const markFor = (quote) => (quote ? quoteMark.get(quote) || null : null);
  const on = (markId) => (markId && activeMark === markId ? ' on' : '');
  const hover = (markId) => (markId ? { onMouseEnter: () => onMarkEnter(markId), onMouseLeave: () => onMarkLeave(markId) } : {});

  const classifications = [...(analysis.classifications || [])].sort((a, b) => severityRank(b.severity) - severityRank(a.severity) || b.confidence - a.confidence);
  const callback = callbackNumber(analysis, call);
  const notMentioned = <span className="muted">Not mentioned</span>;
  const entityRows = [
    ['Caller name', analysis.caller?.name, FIELD.callerName],
    ['Caller role', callerRoleLabel(analysis.caller?.role), FIELD.role],
    ['Callback number', callback && (
      <>
        {callback.number}{' '}
        {callback.matchesCallerId && (
          <span className="badge b-good" title="Matches Teams caller ID">
            <Icon name="check" />
            Matches caller ID
          </span>
        )}
        {callback.fromCallerId && (
          <span className="caption" data-testid="callback-from-caller-id" title="The caller did not say a callback number; this is the Teams caller ID">
            from caller ID
          </span>
        )}
      </>
    ), callback?.fromCallerId ? null : FIELD.callback],
    ['Email', analysis.caller?.email, FIELD.email],
    [terms.community, analysis.property?.communityName, FIELD.community],
    ['Street address', analysis.property?.streetAddress, FIELD.street],
    ['Unit or lot', analysis.property?.unitOrLot, FIELD.unit],
    ['Account reference', analysis.caller?.accountReference, FIELD.account],
  ];

  const series = sentimentSeries(analysis);
  const metrics = analysis.talkMetrics;
  const talkTotal = metrics ? metrics.callerTalkMs + metrics.agentTalkMs + metrics.silenceMs : 0;
  const agentCount = new Set((transcript?.segments || []).filter((s) => s.speakerRole === 'Agent').map((s) => s.speakerLabel)).size;
  const talk = talkTotal
    ? [
        ['Caller', Math.round((metrics.callerTalkMs / talkTotal) * 100), SPEAKER_COLORS[0]],
        [agentCount > 1 ? 'Agents' : 'Agent', Math.round((metrics.agentTalkMs / talkTotal) * 100), SPEAKER_COLORS[1]],
        ['Hold and silence', Math.round((metrics.silenceMs / talkTotal) * 100), 'var(--surface-3)'],
      ]
    : [];
  const quotes = evidenceCounts(classifications, evidence);
  const language = languageName(transcript?.language);
  const confidence = transcript?.languageConfidence != null ? `${Math.round(transcript.languageConfidence * 100)}%` : null;
  const method = transcript?.languageDetectionMethod;
  const transcriber = transcriptionLabel(transcript?.provider);
  const redaction = redactionPhrase(transcript);

  return (
    <aside className="c-5">
      <section className="card ai" aria-label="AI analysis" data-testid="ai-panel">
        <div className="card-head" style={{ paddingBottom: 12, borderBottom: '1px solid var(--divider)' }}>
          <div>
            <div className="t row" style={{ gap: 8 }}>
              <Icon name="sparkle" />
              AI analysis
            </div>
            <div className="d" id="ai-sub">
              {providerLabel(analysis.provider)}
              {taxonomyName ? ` · ${taxonomyName}` : ''}
            </div>
          </div>
          <div className="acts">
            {quotes.total > 0 &&
              (quotes.verified === quotes.total ? (
                <Badge tone="good" icon="check">
                  Evidence verified
                </Badge>
              ) : (
                <Badge tone="warning" icon="alert">
                  {quotes.total - quotes.verified} of {quotes.total} quotes not verified
                </Badge>
              ))}
          </div>
        </div>
        <div id="ai">
          <div className="ai-sec">
            <h3>Summary</h3>
            <p className="small" style={{ lineHeight: 1.6 }}>
              {analysis.summaryDetailed || analysis.summaryShort || 'No summary.'}
            </p>
          </div>
          <div className="ai-sec">
            <h3>
              Classifications <span className="r caption">{classifications.length} {classifications.length === 1 ? 'label' : 'labels'}, multi-label</span>
            </h3>
            {classifications.map((c, i) => {
              const quote = (c.evidence || [])[0];
              const markId = markFor(quote);
              return (
                <div key={`${c.categoryId}-${i}`} className={`cls-item${on(markId)}`} data-evf={markId || undefined} {...hover(markId)}>
                  <div className="row between" style={{ alignItems: 'flex-start', gap: 8 }}>
                    <div className="nm">
                      {c.categoryName}
                      {c.subcategoryName && (
                        <>
                          {' '}
                          <span style={{ fontWeight: 400 }} className="sub">
                            › {c.subcategoryName}
                          </span>
                        </>
                      )}
                    </div>
                    <SeverityLabel severity={c.severity} />
                  </div>
                  <div className="row between" style={{ gap: 8 }}>
                    <Quote quote={quote} markId={markId} speakerName={speakerName} onQuote={onQuote} />
                    <span className="conf" title="Model confidence">
                      <span className="bar">
                        <span style={{ width: `${c.confidence * 100}%` }} />
                      </span>
                      {Math.round(c.confidence * 100)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ai-sec">
            <h3>Caller and property</h3>
            {entityRows.map(([label, value, field]) => {
              const quote = value ? quoteFor(field) : null;
              const markId = markFor(quote);
              return (
                <div key={label} className={`field-row${on(markId)}`} data-evf={markId || undefined} {...hover(markId)}>
                  <span className="fl">{label}</span>
                  <span>
                    {value || notMentioned}
                    {quote && (
                      <>
                        <br />
                        <Quote quote={quote} markId={markId} speakerName={speakerName} onQuote={onQuote} />
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="ai-sec">
            <h3>Sentiment</h3>
            {series ? (
              <LineChart
                labels={series.labels}
                xName="Call time"
                height={150}
                ml={64}
                yMin={-1}
                yMax={1}
                yStep={1}
                yFmt={(x) => (x > 0 ? 'Positive' : x < 0 ? 'Negative' : 'Neutral')}
                vFmt={(x) => `${x > 0 ? '+' : ''}${x.toFixed(1)}`}
                series={[
                  { name: 'Caller', values: series.caller, color: SPEAKER_COLORS[0] },
                  ...(series.agent ? [{ name: agentCount > 1 ? 'Agents' : 'Agent', values: series.agent, color: SPEAKER_COLORS[1] }] : []),
                ]}
              />
            ) : (
              <p className="caption">No sentiment timeline for this call.</p>
            )}
            <p className="caption mt-2">{sentimentNote(analysis.sentiment)}</p>
          </div>
          <div className="ai-sec">
            <h3>
              Action items <span className="r caption">{(analysis.actionItems || []).length}</span>
            </h3>
            {(analysis.actionItems || []).map((a, i) => {
              const quote = quoteFor(`actionItems[${i}]`);
              const markId = markFor(quote);
              const owner = a.owner === 'Agent' && quote && quote.speakerRole === 'Agent' ? speakerName(quote) : OWNER_LABEL[a.owner] || a.owner;
              return (
                <div key={i} className={`act${on(markId)}`} data-evf={markId || undefined} {...hover(markId)}>
                  <input type="checkbox" disabled aria-label="Action item; Vertolytics doesn't track whether it is done" title="Vertolytics doesn't track whether action items are done" style={{ accentColor: 'var(--accent)', marginTop: 3 }} />
                  <div>
                    <div className="small strong">{a.description}</div>
                    <div className="caption">
                      {owner} · {a.dueHint || 'No date given'}
                    </div>
                    <Quote quote={quote} markId={markId} speakerName={speakerName} onQuote={onQuote} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="ai-sec">
            <h3>Language</h3>
            <div className="field-row">
              <span className="fl">Detected language</span>
              <span>
                <strong>{language || 'Not detected'}</strong>
              </span>
            </div>
            <div className="field-row">
              <span className="fl">Detection method</span>
              <span>
                {methodLabel(method)} {confidence && <span className="caption">({method === 'LanguageModelConfirmation' ? `${confidence} speech engine` : confidence})</span>}
              </span>
            </div>
            <p className="caption" style={{ marginTop: 4 }}>
              {method === 'LanguageModelConfirmation'
                ? `${transcriber}'s language identification was below the confidence threshold, so ${providerLabel(analysis.provider).replace(/ via .*/, '')} confirmed ${language || 'the language'} from the transcript. Analysis is written in English.`
                : method === 'SpeechEngineLanguageIdentification'
                ? `${transcriber} language identification; above the confidence threshold, so no model confirmation was needed.`
                : 'Analysis is written in English.'}
            </p>
          </div>
          {talk.length > 0 && (
            <div className="ai-sec">
              <h3>
                Talk time <span className="r caption">computed from timestamps</span>
              </h3>
              <div style={{ display: 'flex', gap: 2, height: 10 }}>
                {talk.map(([label, pct, color]) => (
                  <span key={label} title={`${label} ${pct}%`} style={{ flex: pct, background: color, borderRadius: 2 }} />
                ))}
              </div>
              <div className="legend mt-2">
                {talk.map(([label, pct, color]) => (
                  <span key={label}>
                    <i style={{ background: color }} />
                    {label} {pct}%
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="ai-sec">
            <h3>How this was produced</h3>
            {[
              ['Transcribed', transcript ? `${transcriber}${transcript.providerModel ? ` (${transcript.providerModel})` : ''}, ${fmtTime(transcript.createdAt)}` : 'Not transcribed'],
              ['Analyzed', `${providerLabel(analysis.provider)}, ${fmtTime(analysis.analyzedAt)}`],
              ['Taxonomy', taxonomyName || `Version ${analysis.taxonomyVersionNumber}`],
              ['Evidence', quotes.total ? `${quotes.verified} of ${quotes.total} quotes matched the transcript` : 'No quotes'],
              ['Redaction', redaction ? `${redaction} removed before saving` : 'Nothing to redact'],
              ['Kept until', retentionText(transcript, analysis)],
            ].map(([label, value]) => (
              <div key={label} className="field-row">
                <span className="fl">{label}</span>
                <span>{value}</span>
              </div>
            ))}
            {actions}
          </div>
        </div>
      </section>
    </aside>
  );
}

/** Quotes behind the analysis and how many the evidence verifier matched in the transcript. */
export function evidenceCounts(classifications, evidence) {
  const all = [...(classifications || []).flatMap((c) => c.evidence || []), ...(evidence || [])];
  return { total: all.length, verified: all.filter((q) => q.verified === true).length };
}

const SEVERITY_RANK = { Informational: 0, Standard: 1, Within48Hours: 2, Immediate: 3 };
const severityRank = (severity) => SEVERITY_RANK[severity] ?? 0;

